import { IAppContext } from 'App/AppContext';
import {
  mapFromProcessingActivities,
  mapFromProcessingActivity,
  mapFromProcessingActivityContext,
  mapToProcessingActivity,
} from 'mappings/processingActivityMapping';
import { mapToTask } from 'mappings/taskMapping';
import { ProcessingActivityContextDTO } from 'models/dto/processingActivity/processingActivityContextDTO';
import { ProcessingActivityControlDTO } from 'models/dto/processingActivity/processingActivityControlDTO';
import ProcessingActivityDTO from 'models/dto/processingActivity/processingActivityDTO';
import { ProcessingActivityProcessDTO } from 'models/dto/processingActivity/processingActivityProcessDTO';
import { ProcessingActivityRiskDTO } from 'models/dto/processingActivity/processingActivityRiskDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import ProcessingActivity, { ComplianceIssue, PrivacyFramework } from 'models/processingActivity';
import { ProcessingActivityContext } from 'models/processingActivityContext';
import Task from 'models/tasks/task';
import AppError from 'utils/appError';
import http from './httpService';

export async function apiGetProcessingActivities(
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ProcessingActivity[]> {
  try {
    const ar = await http.get<ProcessingActivityDTO[]>(`/processingactivities`, http.getRequestConfig(accessToken));

    return mapFromProcessingActivities(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetProcessingActivity(
  id: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ProcessingActivity | undefined> {
  try {
    const ar = await http.get<ProcessingActivityDTO>(`/processingactivities/${id}`, http.getRequestConfig(accessToken));
    if (!ar.data) return undefined;

    return mapFromProcessingActivity(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddProcessingActivity(
  processingActivity: ProcessingActivity,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ProcessingActivity> {
  try {
    const dto = mapToProcessingActivity(processingActivity);
    const ar = await http.post<ProcessingActivityDTO>(`/processingactivities`, dto, http.getRequestConfig(accessToken));

    return mapFromProcessingActivity(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetProcessingActivityCode(accessToken: string, code: string): Promise<string> {
  try {
    const ar = await http.post<string>(
      `/processingactivities/code`,
      { valueString: code },
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateProcessingActivity(
  processingActivity: ProcessingActivity,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ProcessingActivity> {
  try {
    const dto = mapToProcessingActivity(processingActivity);
    const ar = await http.put<ProcessingActivityDTO>(`/processingactivities`, dto, http.getRequestConfig(accessToken));

    return mapFromProcessingActivity(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveProcessingActivity(
  processingActivity: ProcessingActivity,
  accessToken: string,
): Promise<boolean> {
  try {
    await http.post(
      `/processingactivities/delete/${processingActivity.processingActivityId}`,
      null,
      http.getRequestConfig(accessToken),
    );

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//
// Context
//
export async function apiGetProcessingActivityContext(
  processingActivityId: number,
  accessToken: string,
  appContext: IAppContext,
): Promise<ProcessingActivityContext> {
  try {
    const ar = await http.get<ProcessingActivityContextDTO>(
      `/processingactivities/context/${processingActivityId}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromProcessingActivityContext(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddProcessToProcessingActivity(
  processingActivityId: number,
  processIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ProcessingActivityProcessDTO(processingActivityId, processIdTo);
    await http.post<ProcessingActivityProcessDTO>(
      `/processingactivities/process?add=${true}`,
      body,
      http.getRequestConfig(accessToken),
    );

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveProcessFromProcessingActivity(
  processingActivityId: number,
  processId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ProcessingActivityProcessDTO(processingActivityId, processId);
    await http.post<ProcessingActivityProcessDTO>(
      `/processingactivities/process?add=${false}`,
      body,
      http.getRequestConfig(accessToken),
    );

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddControlToProcessingActivity(
  processingActivityId: number,
  controlId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ProcessingActivityControlDTO(processingActivityId, controlId);
    await http.post<ProcessingActivityControlDTO>(
      `/processingactivities/control?add=${true}`,
      body,
      http.getRequestConfig(accessToken),
    );

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveControlFromProcessingActivity(
  processingActivityId: number,
  controlId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ProcessingActivityControlDTO(processingActivityId, controlId);
    await http.post<ProcessingActivityControlDTO>(
      `/processingactivities/control?add=${false}`,
      body,
      http.getRequestConfig(accessToken),
    );

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddRiskToProcessingActivity(
  processingActivityId: number,
  riskId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ProcessingActivityRiskDTO(processingActivityId, riskId);
    await http.post<ProcessingActivityRiskDTO>(
      `/processingactivities/risk?add=${true}`,
      body,
      http.getRequestConfig(accessToken),
    );

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveRiskFromProcessingActivity(
  processingActivityId: number,
  riskId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ProcessingActivityRiskDTO(processingActivityId, riskId);
    await http.post<ProcessingActivityRiskDTO>(
      `/processingactivities/risk?add=${false}`,
      body,
      http.getRequestConfig(accessToken),
    );

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetProcessingActivitiesNoRelations(
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ProcessingActivity[]> {
  try {
    const ar = await http.get<ProcessingActivityDTO[]>(
      `/processingactivities/norelations`,
      http.getRequestConfig(accessToken),
    );

    return mapFromProcessingActivities(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetProcessingActivityCompliance(
  accessToken: string,
  processingActivityId: number,
  privacyFramework: PrivacyFramework
): Promise<ComplianceIssue[]> {
  try {
    const ar = await http.get<ComplianceIssue[]>(
      `/processingactivities/compliance/${processingActivityId}/${privacyFramework}`,
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetProcessingActivityReport(
  id: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<ProcessingActivity> {
  try {
    const ar = await http.get<ProcessingActivityDTO>(
      `/processingactivities/${id}/report`,
      http.getRequestConfig(accessToken),
    );

    return mapFromProcessingActivity(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateProcessingActivityApproval(
  task: Task,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ProcessingActivity> {
  try {
    const taskDTO = mapToTask(task);
    const ar = await http.post<ProcessingActivityDTO>(
      `/processingactivities/approval`,
      taskDTO,
      http.getRequestConfig(accessToken),
    );

    return mapFromProcessingActivity(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
